import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Icon } from 'nallian-shared-ui/lib/components';
import { CardFlex } from 'nallian-shared-ui/lib/layout/UserApps';
import CommunityInfoModal from './CommunityInfoModal';
import ViewMoreAppsModal from './ViewMoreAppsModal';
import AppIcon from './AppIcon';

const MAX_VISIBLE_APPS = 6;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
  padding: 1.5rem;
  margin: 0.75rem;
  flex: 0 0 calc(33.333% - 1.5rem);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const CommunityName = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const InfoIcon = styled(Icon)`
  margin-left: 0.5rem !important;
  color: #2185d0 !important;
  cursor: pointer;
  &:hover {
    color: #1678c2 !important;
  }
`;

const AppCount = styled.p`
  color: #666;
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  justify-items: center;

  @media (min-width: 1920px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const AppIconWrapper = styled(CardFlex)`
  margin: 0;
  width: auto;
`;

const ViewMoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

const ViewMoreIconWrapper = styled.div`
  width: 95px;
  height: 95px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e0e0e0 0%, #c0c0c0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
`;

const DotsRow = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #666;
`;

const ViewMoreLabel = styled.span`
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #333;
  text-align: center;
`;

const CommunityCard = ({ community, apps }) => {
  const { t } = useTranslation();
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [viewMoreModalOpen, setViewMoreModalOpen] = useState(false);

  const hasMoreApps = apps.length > MAX_VISIBLE_APPS;
  // If more than 6 apps, show 5 + View More icon (total 6 icons)
  const visibleApps = hasMoreApps ? apps.slice(0, MAX_VISIBLE_APPS - 1) : apps;
  const appCount = apps.length;

  return (
    <Card>
      <CardHeader>
        <CommunityName>
          {community.name} {t('community')}
        </CommunityName>
        <InfoIcon
          margin="0 0 0.4rem 0"
          name="question circle outline"
          onClick={() => setInfoModalOpen(true)}
          title={t('communityInfo')}
        />
      </CardHeader>

      <AppCount>
        {appCount === 1
          ? t('appCountSingular', { count: appCount })
          : t('appCountPlural', { count: appCount })}
      </AppCount>

      <AppsGrid>
        {visibleApps.map(app => (
          <AppIconWrapper
            big
            direction="column"
            alignItems="center"
            key={`${community.id}_${app.appInstallationTag}`}
          >
            <AppIcon app={app} />
          </AppIconWrapper>
        ))}

        {hasMoreApps && (
          <AppIconWrapper big direction="column" alignItems="center">
            <ViewMoreContainer onClick={() => setViewMoreModalOpen(true)}>
              <ViewMoreIconWrapper>
                <DotsRow>
                  <Dot />
                  <Dot />
                  <Dot />
                </DotsRow>
              </ViewMoreIconWrapper>
              <ViewMoreLabel>{t('viewMore')}</ViewMoreLabel>
            </ViewMoreContainer>
          </AppIconWrapper>
        )}
      </AppsGrid>

      <CommunityInfoModal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        community={community}
      />

      <ViewMoreAppsModal
        open={viewMoreModalOpen}
        onClose={() => setViewMoreModalOpen(false)}
        community={community}
        apps={apps}
      />
    </Card>
  );
};

CommunityCard.propTypes = {
  community: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    tag: PropTypes.string,
  }).isRequired,
  apps: PropTypes.array.isRequired,
};

export default CommunityCard;
